import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { RecordFormModal } from "@/components/RecordFormModal";
import { normalizeUrl } from "@/lib/normalizeUrl";
import { Loader2, Send, Sparkles, AlertTriangle, Globe2, CheckCircle2, HelpCircle, XCircle, FileEdit, Save, X, Package, Film, Camera, Receipt, PlayCircle, Wrench } from "lucide-react";

const STATUS_DOT = { confirmed: "bg-emerald-500", inferred: "bg-amber-500", missing: "bg-[#D6D3D1]" };
const STATUS_LABEL = { confirmed: "Confirmed", inferred: "Inferred", missing: "Missing" };
const CONFIDENCE_CLS = { High: "bg-emerald-500/10 text-emerald-700", Medium: "bg-amber-500/10 text-amber-700", Low: "bg-red-500/10 text-red-600" };

const FIELD_LABELS = {
  client_name: "Client", contact: "Contact", project_name: "Project name", objective: "Objective",
  audience: "Audience", project_type_raw: "Project type (as described)", deliverables: "Deliverables",
  num_films: "Number of films", duration: "Approx. duration", shoot_location: "Shoot location",
  shoot_dates: "Shoot dates", shoot_days: "Shoot days", interviews: "Interviews", b_roll: "B-roll",
  photography: "Photography", animation: "Animation", voiceover: "Voiceover", drone: "Drone",
  editing: "Editing", output_formats: "Output formats", usage_platform: "Usage platform",
  deadline: "Deadline", budget: "Budget", reference_links: "Reference links",
  international: "International/domestic", special_constraints: "Special constraints",
};

function ConfidenceBadge({ value }) {
  return <span data-testid="confidence-badge" className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CONFIDENCE_CLS[value] || CONFIDENCE_CLS.Low}`}>{value}</span>;
}

function FactRow({ field, data }) {
  const status = data?.status || "missing";
  return (
    <div className="flex items-start gap-2 py-1" data-testid={`fact-row-${field}`}>
      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_DOT[status]}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">{FIELD_LABELS[field] || field} · <span className={status === "missing" ? "text-[#A8A29E]" : status === "inferred" ? "text-amber-600" : "text-emerald-600"}>{STATUS_LABEL[status]}</span></div>
        <div className="text-xs text-[#1C1C1E]">{data?.value || "—"}</div>
      </div>
    </div>
  );
}

export default function AiIntakeWorkspace({ onExit }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [rawText, setRawText] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [openingQuote, setOpeningQuote] = useState(false);

  const analyze = async () => {
    const text = rawText.trim();
    if (!text) return;
    setLoading(true);
    try {
      const r = await api.post("/v1/ai-intake/analyze", { raw_text: text });
      setSession(r.data);
    } catch {
      toast.error("Could not analyse this brief", { description: "Try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const clarify = async () => {
    const text = answerText.trim();
    if (!text || !session) return;
    setLoading(true);
    try {
      const r = await api.post(`/v1/ai-intake/${session.id}/clarify`, { answers_text: text });
      setSession(r.data);
      setAnswerText("");
    } catch {
      toast.error("Could not process your answers");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = async () => {
    const r = await api.get(`/v1/ai-intake/${session.id}/prefill`);
    setPrefill(r.data);
    setShowCreateModal(true);
  };

  const handleCreated = async (record) => {
    await api.post(`/v1/ai-intake/${session.id}/link-project`, { project_id: record.id }).catch(() => {});
    setSession((s) => ({ ...s, status: "project_created", linked_project_id: record.id }));
  };

  const openQuoteBuilder = async () => {
    // Item 1 (2026-02, Brief -> auto-seeded draft quote): now seeds a real quote_drafts
    // record server-side (package + any AI-parsed gear/crew matched against the live rate
    // card, unmatched items added as blank-rate custom lines) and opens it via the Quote
    // Builder's EXISTING `?draft_id=` deep link — the PM lands on a quote that's already
    // populated instead of an empty package picker, but still reviews/edits everything
    // themselves before Publish/Save/Export, nothing is auto-submitted from here.
    setOpeningQuote(true);
    try {
      const r = await api.post(`/v1/ai-intake/${session.id}/seed-quote-draft`);
      if (r.data.unmatched_gear_count > 0) {
        toast.info(`${r.data.unmatched_gear_count} gear item(s) from the brief need a manual rate — look for the "custom" lines on the Quote Builder.`);
      }
      navigate(`/commercial?draft_id=${r.data.draft_id}`);
    } catch {
      toast.error("Could not open the Quote Builder", { description: "Try again in a moment." });
    } finally {
      setOpeningQuote(false);
    }
  };

  const saveDraft = async () => {
    await api.patch(`/v1/ai-intake/${session.id}`, { status: "draft" });
    toast.success("Saved as draft — find it again from Ask AI later.");
  };

  const startOver = () => { setSession(null); setRawText(""); setAnswerText(""); };

  const cancelSession = async () => {
    if (session) await api.patch(`/v1/ai-intake/${session.id}`, { status: "cancelled" }).catch(() => {});
    startOver();
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0" data-testid="ai-intake-workspace">
      {/* LEFT — conversation */}
      <div className="lg:w-1/2 flex flex-col rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] min-h-0">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-4 py-3">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#A8A29E]"><Sparkles className="h-3.5 w-3.5" /> Analyse New Client Brief</div>
          <button onClick={onExit} data-testid="intake-exit-btn" className="text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!session && (
            <div className="text-xs text-[#57534E] leading-relaxed">Paste the client's raw brief below — an email, WhatsApp message, meeting notes, or a quick summary. The AI will assist with category, production model, and missing information — nothing is saved until you confirm.</div>
          )}
          {session?.conversation?.map((turn, i) => (
            <div key={i} className={`rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${turn.role === "ai" ? "bg-[#FFF8E1] text-[#1C1C1E]" : "bg-[#F5F5F4] text-[#57534E]"}`} data-testid={`intake-turn-${i}`}>
              <span className="text-[9px] uppercase tracking-wider text-[#A8A29E] block mb-1">{turn.role === "ai" ? "AI" : turn.role === "pm_answer" ? "You (answers)" : "You"}</span>
              {turn.text}
            </div>
          ))}
          {session?.status === "clarifying" && session.clarifying_questions?.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="intake-clarifying-questions">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><HelpCircle className="h-3 w-3" /> A few quick questions</div>
              <ul className="list-disc list-inside text-xs text-[#1C1C1E] space-y-0.5">
                {session.clarifying_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div className="border-t border-[#E7E5E4] p-3">
          {!session ? (
            <div className="space-y-2">
              <textarea data-testid="intake-raw-text-input" value={rawText} onChange={(e) => setRawText(e.target.value)} rows={5}
                placeholder="Paste the client's brief here…" disabled={loading}
                className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-xs text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50 resize-none" />
              <button data-testid="intake-analyze-btn" onClick={analyze} disabled={loading || !rawText.trim()}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-2 text-xs font-medium text-white hover:bg-[#1C1C1E]/90 disabled:opacity-40">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Analyse Brief
              </button>
            </div>
          ) : session.status === "clarifying" ? (
            <div className="space-y-2">
              <textarea data-testid="intake-answer-input" value={answerText} onChange={(e) => setAnswerText(e.target.value)} rows={3}
                placeholder="Answer the questions above (plain text is fine)…" disabled={loading}
                className="w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-xs text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50 resize-none" />
              <div className="flex gap-2">
                <button data-testid="intake-submit-answers-btn" onClick={clarify} disabled={loading || !answerText.trim()}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-2 text-xs font-medium text-white hover:bg-[#1C1C1E]/90 disabled:opacity-40">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit Answers
                </button>
                <button data-testid="intake-skip-to-review-btn" onClick={() => setSession((s) => ({ ...s, status: "ready_for_review" }))}
                  className="rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:border-[#FFC817]">Skip to Review</button>
              </div>
            </div>
          ) : (
            <div className="text-xs text-[#57534E] text-center py-1">Review the recommendation on the right, then choose an action below.</div>
          )}
        </div>
      </div>

      {/* RIGHT — live brief preview */}
      <div className="lg:w-1/2 flex flex-col rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] min-h-0 overflow-y-auto" data-testid="intake-brief-preview">
        {!session ? (
          <div className="flex-1 flex items-center justify-center text-xs text-[#A8A29E] p-8 text-center">The live Project Brief preview will appear here as soon as you analyse a brief.</div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[#A8A29E]">Overall Confidence</div>
              <ConfidenceBadge value={session.overall_confidence} />
            </div>

            {session.international_flag && (
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 px-3 py-2 flex items-start gap-2" data-testid="international-banner">
                <Globe2 className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">International brief — routed to Manual/Partner Review. Foreign-currency handling needed; no domestic assumptions applied.</div>
              </div>
            )}
            {session.production_model_recommendation?.model === "Manual Review" && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 flex items-start gap-2" data-testid="manual-review-banner">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <div className="text-xs text-red-700">This brief needs Manual Review — too incomplete/contradictory (or international) for the AI to recommend a production model safely.</div>
              </div>
            )}

            <div className="rounded-lg border border-[#E7E5E4] p-3" data-testid="category-recommendation">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Recommended Category</div>
                <ConfidenceBadge value={session.category_recommendation?.confidence} />
              </div>
              <div className="text-sm font-semibold text-[#1C1C1E]">{session.category_recommendation?.category}</div>
              {session.category_recommendation?.secondary_category && <div className="text-[11px] text-[#A8A29E]">Secondary: {session.category_recommendation.secondary_category}</div>}
              <div className="text-xs text-[#57534E] mt-1">{session.category_recommendation?.reason}</div>
              {session.category_recommendation?.alternative && <div className="text-[11px] text-[#A8A29E] mt-1">Alternative: {session.category_recommendation.alternative}</div>}
            </div>

            <div className="rounded-lg border border-[#E7E5E4] p-3" data-testid="production-model-recommendation">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E]">Recommended Production Model</div>
                <ConfidenceBadge value={session.production_model_recommendation?.confidence} />
              </div>
              <div className="text-sm font-semibold text-[#1C1C1E]">{session.production_model_recommendation?.model || "Unresolved"}</div>
              <div className="text-xs text-[#57534E] mt-1">{session.production_model_recommendation?.reason}</div>
            </div>

            <div className="rounded-lg border border-[#E7E5E4] p-3" data-testid="package-recommendation">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] flex items-center gap-1"><Package className="h-3 w-3" /> Recommended Package</div>
                <ConfidenceBadge value={session.package_recommendation?.confidence} />
              </div>
              {session.package_recommendation?.package_id ? (
                <>
                  <div className="text-sm font-semibold text-[#1C1C1E]">{session.package_recommendation.package_name} <span className="text-[11px] font-mono text-[#A8A29E]">({session.package_recommendation.package_id})</span></div>
                  {(session.package_recommendation.camera_kit_recommendation || session.package_recommendation.camera_units) && (
                    <div className="text-[11px] text-[#A8A29E] flex items-center gap-1 mt-0.5"><Camera className="h-3 w-3" />
                      {session.package_recommendation.camera_units ? `${session.package_recommendation.camera_units} camera unit(s)` : ""}
                      {session.package_recommendation.camera_kit_recommendation ? ` · ${session.package_recommendation.camera_kit_recommendation} kit` : ""}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm font-semibold text-[#A8A29E]">No confident match — select manually in the Quote Builder</div>
              )}
              <div className="text-xs text-[#57534E] mt-1">{session.package_recommendation?.reason}</div>
              <div className="text-[10px] text-[#A8A29E] mt-1 italic">Package IDs only — the Commercial Engine (Rate Card) sets all pricing, never the AI.</div>
            </div>

            {session.gear_requirements?.length > 0 && (
              <div className="rounded-lg border border-[#E7E5E4] p-3" data-testid="gear-requirements">
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] flex items-center gap-1 mb-1.5"><Wrench className="h-3 w-3" /> Gear &amp; Crew Mentioned</div>
                <div className="flex flex-wrap gap-1.5">
                  {session.gear_requirements.map((g, i) => (
                    <span key={i} data-testid={`gear-requirement-${i}`} className="inline-flex items-center rounded-full border border-[#E7E5E4] bg-[#F5F5F4] px-2 py-1 text-[11px] text-[#57534E]">{g}</span>
                  ))}
                </div>
                <div className="text-[10px] text-[#A8A29E] mt-1.5 italic">Matched against the Rate Card automatically when you open the Quote Builder — unmatched items are added as blank-rate custom lines for you to price.</div>
              </div>
            )}

            {session.video_references?.length > 0 && (
              <div className="rounded-lg border border-[#E7E5E4] p-3" data-testid="video-references">
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] flex items-center gap-1 mb-1.5"><Film className="h-3 w-3" /> Similar NJM Work (Video Library)</div>
                <div className="flex flex-wrap gap-1.5">
                  {session.video_references.map((v) => (
                    <a key={v.id} href={normalizeUrl(v.video_url)} target="_blank" rel="noopener noreferrer" data-testid={`video-ref-${v.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-[#E7E5E4] bg-[#F5F5F4] px-2 py-1 text-[11px] text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
                      <PlayCircle className="h-3 w-3" /> {v.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1">Extracted Facts</div>
              <div className="rounded-lg border border-[#E7E5E4] divide-y divide-[#F5F5F4] px-3">
                {Object.entries(session.extracted_facts || {}).map(([field, data]) => <FactRow key={field} field={field} data={data} />)}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E7E5E4]">
              <button data-testid="intake-create-project-btn" onClick={openCreate} disabled={session.status === "project_created"}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#B45309] px-3 py-2 text-xs font-medium text-white hover:bg-[#B45309]/90 disabled:opacity-40">
                <FileEdit className="h-3.5 w-3.5" /> {session.status === "project_created" ? "Project Created" : "Create Project Brief"}
              </button>
              {session.status === "project_created" && (
                <button data-testid="intake-open-quote-builder-btn" onClick={openQuoteBuilder} disabled={openingQuote}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-3 py-2 text-xs font-medium text-white hover:bg-[#1C1C1E]/90 disabled:opacity-40">
                  {openingQuote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />} Open Quote Builder
                </button>
              )}
              <button data-testid="intake-ask-more-btn" onClick={() => setSession((s) => ({ ...s, status: "clarifying" }))}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:border-[#FFC817]">
                <HelpCircle className="h-3.5 w-3.5" /> Ask More Questions
              </button>
              <button data-testid="intake-save-draft-btn" onClick={saveDraft} className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:border-[#FFC817]">
                <Save className="h-3.5 w-3.5" /> Save as Draft
              </button>
              <button data-testid="intake-cancel-btn" onClick={cancelSession} className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] px-3 py-2 text-xs text-[#57534E] hover:border-red-400 hover:text-red-600">
                <XCircle className="h-3.5 w-3.5" /> Cancel
              </button>
            </div>
            {session.status === "project_created" && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="intake-project-created-note">
                <CheckCircle2 className="h-3.5 w-3.5" /> Project created and linked to this brief.
              </div>
            )}
          </div>
        )}
      </div>

      {prefill && (
        <RecordFormModal
          type="project" mode="create" open={showCreateModal} onClose={() => setShowCreateModal(false)}
          initialCreateValues={prefill} onCreated={handleCreated}
        />
      )}
    </div>
  );
}
